import { DateTime } from "luxon";

const MINIMUM_DATE_INCREMENT_MILLISECONDS = 1000;
const DOB_ISO_VALUE_PATTERN = /^\d{4}-\d{2}-\d{2}(?:$|T)/;

/**
 * Null-safe date comparison with handling for strings representing a data.
 *
 * Items are sorted by most to least recent (descending order).
 *
 * Items that are null are sorted to the end of the list.
 */
export const dateCompareIfNotNull = (
  a: Date | string | null | undefined,
  b: Date | string | null | undefined
): number => {
  if (a && !b) return 1;
  if (b && !a) return -1;
  if (!a && !b) return 0;

  const dateA = new Date(a);
  const dateB = new Date(b);

  return dateA < dateB ? 1 : -1;
};

export const toDobIsoDate = (value: string | DateTime): string => {
  if (DateTime.isDateTime(value)) {
    const isoDate = value.toISODate();
    if (!value.isValid || !isoDate) throw new Error("Invalid date-only value");
    return isoDate;
  }

  if (!DOB_ISO_VALUE_PATTERN.test(value)) {
    throw new Error("Invalid date-only value");
  }

  const dateOnly = DateTime.fromISO(value, { setZone: true });
  const isoDate = dateOnly.toISODate();
  if (!dateOnly.isValid || !isoDate) throw new Error("Invalid date-only value");
  return isoDate;
};

export const normalizeDobInput = (value: string | null | undefined): string => {
  if (!value) return "";

  try {
    return toDobIsoDate(value);
  } catch {
    return value;
  }
};

export const isFutureDobDate = (value: string | DateTime): boolean =>
  toDobIsoDate(value) > DateTime.local().toISODate();

/**
 * Returns true if given date is in the future and false if the given date is in the past.
 */
export const isFutureDate = (date: string | Date) => {
  const until = new Date(date);
  const now = new Date();

  if (until > now) {
    return true;
  }

  return false;
};

/**
 * Returns a computed local time that is guaranteed to be after the given timestamp
 */
export const getComputedLocalTime = (minimumDate?: Date | null): Date => {
  const currentDate = new Date();

  if (!minimumDate) return currentDate;

  if (currentDate > minimumDate) {
    return currentDate;
  }

  const computedCreationTime = new Date(
    minimumDate.getTime() + MINIMUM_DATE_INCREMENT_MILLISECONDS
  );

  return computedCreationTime;
};

export const msToTime = (
  value: number,
  format?: "singlePadMinutes"
): string => {
  if (isNaN(value)) {
    return "0:00";
  }
  const nearestSecond = Math.round(value / 1000);
  const seconds = nearestSecond % 60;
  const minutes = Math.floor(nearestSecond / 60);

  const formattedMinutes =
    format === "singlePadMinutes"
      ? minutes.toString()
      : minutes.toString().padStart(2, "0");

  const formattedSeconds = seconds.toString().padStart(2, "0");

  return `${formattedMinutes}:${formattedSeconds}`;
};
